import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import styles from './AIInput.module.scss';
import Icon, { IconGlyph } from '../Icon';
import { useTextareaHeight } from '../../hooks/useTextareaHeight';
import { LengthInChars } from '../../utilities/constants/common';
import {
  SUBJECT_GROUP_INPUT_TAGS,
  DEFAULT_SUBJECT_GROUP_INPUT_TAGS,
} from '../../utilities/constants/subject-group';
import { Phrases } from '../../utilities/constants/phrases';
import { dataLayerGA } from '../../services/analytics/dataLayerGa';

interface AIInputProps {
  placeholder?: string;
  disabled?: boolean;
  subjectGroup?: string;
  onSubmit: (value: string) => void;
}

const TEXTAREA_CONFIG = {
  MIN_HEIGHT: 30,
  MAX_HEIGHT: 84,
  WRAPPER_HEIGHT: 87,
  DEFAULT_ROWS: 1,
} as const;

const CSS_VARIABLES = {
  AI_INPUT_HEIGHT: '--ai-input-height',
} as const;

const AIInput: React.FC<AIInputProps> = ({
  onSubmit,
  placeholder = Phrases.AI_INPUT_PLACEHOLDER,
  disabled = false,
  subjectGroup,
}) => {
  const [inputValue, setInputValue] = useState('');

  const { textareaRef, adjustHeight, resetHeight } = useTextareaHeight({
    minHeight: TEXTAREA_CONFIG.MIN_HEIGHT,
    maxHeight: TEXTAREA_CONFIG.MAX_HEIGHT,
    wrapperHeight: TEXTAREA_CONFIG.WRAPPER_HEIGHT,
    defaultRows: TEXTAREA_CONFIG.DEFAULT_ROWS,
    cssVariable: CSS_VARIABLES.AI_INPUT_HEIGHT,
  });

  const sendAnalyticsEvent = (eventContext: string = 'Default') => {
    dataLayerGA.generateNextText(eventContext);
  };

  const handleSubmit = (): void => {
    const trimmedValue = inputValue.trim();

    if (!trimmedValue || disabled) return;

    sendAnalyticsEvent();
    onSubmit(trimmedValue);
    setInputValue('');
    resetHeight();
  };

  const handleTagClick = (tagLabel: string): void => {
    if (disabled) return;

    sendAnalyticsEvent(`Tag,${tagLabel}`);
    onSubmit(tagLabel);
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ): void => {
    setInputValue(event.target.value);
    adjustHeight();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLTextAreaElement>
  ): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  const isSubmitDisabled = useMemo(() => {
    const trimmedValue = inputValue.trim();
    const { length } = trimmedValue;

    const isLengthValid =
      length >= LengthInChars.EDITOR_USER_REQUEST_MIN &&
      length <= LengthInChars.EDITOR_USER_REQUEST_MAX;

    return disabled || !trimmedValue || !isLengthValid;
  }, [inputValue, disabled]);

  const inputTags = useMemo(
    () =>
      (subjectGroup && SUBJECT_GROUP_INPUT_TAGS[subjectGroup]) ||
      DEFAULT_SUBJECT_GROUP_INPUT_TAGS,
    [subjectGroup]
  );

  return (
    <div className={classNames(styles.bgWrapper, styles.sticky)}>
      <div className={styles.wrapper}>
        <div className={styles.inputContainer}>
          <div className={styles.iconContainer}>
            <Icon glyph={IconGlyph.AI_INPUT_LOGO} />
          </div>
          <div className={styles.inputWrapper}>
            <textarea
              name='ai-input'
              ref={textareaRef}
              className={styles.input}
              placeholder={placeholder}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              disabled={disabled}
              rows={TEXTAREA_CONFIG.DEFAULT_ROWS}
              aria-label='AI input field'
            />
          </div>
          <button
            className={styles.sendButton}
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            type='button'
            aria-label='Send message'>
            <Icon glyph={IconGlyph.AI_INPUT_SEND} />
          </button>
        </div>
        <div className={styles.tagsContainer}>
          {inputTags.map((tag) => (
            <button
              key={tag}
              className={styles.tag}
              onClick={() => handleTagClick(tag)}
              disabled={disabled}
              type='button'
              aria-label={`Use ${tag} tag`}>
              {tag}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AIInput;
